import permisosService from "@/services/permisosService";
import { mensajeDeError } from "@/utils/permisosErrores";
import { LIMITE_CATALOGO } from "@/constants/permisos";

/**
 * Vuex module for the roles & permissions panel (Trello #385).
 *
 * Every action maps backend errors through `mensajeDeError` and rethrows the
 * resulting `{ mensaje, claves }` so views can toast the message and highlight
 * the offending keys. `cargarMisAcciones` is the exception: ANY failure is
 * swallowed and `puede` fails open — the backend still enforces, the UI only
 * stops hiding buttons.
 */

/** `modo` is 'off' | 'sombra' | 'enforce' when the backend reports it, null otherwise. */
const misAccionesInicial = () => ({
  acciones: [],
  grant: [],
  deny: [],
  catalogoDisponible: null,
  cargado: false,
  modo: null,
});

const estadoInicial = () => ({
  roles: [],
  rolesLoading: false,
  rolesError: null,
  catalogo: { acciones: [], total: 0, loading: false, error: null },
  rolAcciones: { idRol: null, rol: null, claves: [], loading: false },
  overrides: { userId: null, items: [], loading: false },
  pantallas: { idRol: null, rutas: [], menus: [], loading: false },
  misAcciones: misAccionesInicial(),
  guardando: false,
});

const getters = {
  /** Fail-open when the catalog is unavailable; the backend enforces anyway. */
  puede: (state) => (clave) =>
    state.misAcciones.catalogoDisponible === false ? true : state.misAcciones.acciones.includes(clave),
  accionesPorModulo: (state) => {
    const grupos = {};
    state.catalogo.acciones.forEach((a) => {
      (grupos[a.modulo_origen] = grupos[a.modulo_origen] || []).push(a);
    });
    Object.values(grupos).forEach((lista) => lista.sort((a, b) => a.clave.localeCompare(b.clave)));
    return grupos;
  },
  catalogoCargado: (state) =>
    state.catalogo.total > 0 && state.catalogo.acciones.length >= state.catalogo.total,
};

/** An error already shaped by `mensajeDeError` (e.g. rethrown by a nested action). */
const yaMapeado = (error) => Boolean(error) && typeof error.mensaje === "string" && Array.isArray(error.claves);

/** Runs `fn` with a loading flag around it, mapping (once) and rethrowing errors. */
async function conCarga(commit, mutacionLoading, fn) {
  commit(mutacionLoading, true);
  try {
    return await fn();
  } catch (error) {
    throw yaMapeado(error) ? error : mensajeDeError(error);
  } finally {
    commit(mutacionLoading, false);
  }
}

/** Runs a write operation with the shared `guardando` flag. */
const conGuardado = (commit, fn) => conCarga(commit, "SET_GUARDANDO", fn);

/** Refreshes the role list after a write; its failure is surfaced through `rolesError`, never fails the write. */
const refrescarRoles = (dispatch) =>
  dispatch("fetchRoles").catch((error) => console.warn("No se pudo refrescar la lista de roles", error?.mensaje));

const actions = {
  async fetchRoles({ commit }) {
    commit("SET_ROLES_ERROR", null);
    try {
      return await conCarga(commit, "SET_ROLES_LOADING", async () => {
        const roles = await permisosService.listarRoles();
        commit("SET_ROLES", roles || []);
        return roles;
      });
    } catch (mapeado) {
      commit("SET_ROLES_ERROR", mapeado.mensaje);
      throw mapeado;
    }
  },

  /** Loads the whole action catalog, page by page, until `total` is reached. */
  async fetchCatalogo({ commit }, { modulo } = {}) {
    commit("SET_CATALOGO_ERROR", null);
    try {
      return await conCarga(commit, "SET_CATALOGO_LOADING", async () => {
        const acumuladas = [];
        let total = 0;
        let desde = 0;
        do {
          const pagina = await permisosService.listarAcciones({ modulo, limite: LIMITE_CATALOGO, desde });
          const acciones = pagina.acciones || [];
          total = pagina.total || 0;
          acumuladas.push(...acciones);
          // Advance by what was actually received so a short page never skips rows (REL-011).
          desde += acciones.length;
          if (!acciones.length) break;
        } while (acumuladas.length < total);
        commit("SET_CATALOGO", { acciones: acumuladas, total });
        return acumuladas;
      });
    } catch (mapeado) {
      commit("SET_CATALOGO_ERROR", mapeado.mensaje);
      throw mapeado;
    }
  },

  /** Resets the slice to the requested role first so a stale role's keys are never shown. */
  fetchRolAcciones({ commit }, idRol) {
    commit("RESET_ROL_ACCIONES", idRol);
    return conCarga(commit, "SET_ROL_ACCIONES_LOADING", async () => {
      const data = await permisosService.obtenerAccionesDeRol(idRol);
      const claves = (data.acciones || []).map((a) => a.clave);
      commit("SET_ROL_ACCIONES", { idRol, rol: data.rol || null, claves });
      return data;
    });
  },

  guardarRolAcciones({ commit }, { idRol, claves }) {
    return conGuardado(commit, async () => {
      const data = await permisosService.guardarAccionesDeRol(idRol, claves);
      commit("SET_ROL_ACCIONES_CLAVES", claves);
      return data;
    });
  },

  /** Resets the slice to the requested user first so another user's overrides are never shown. */
  fetchOverrides({ commit }, userId) {
    commit("RESET_OVERRIDES", userId);
    return conCarga(commit, "SET_OVERRIDES_LOADING", async () => {
      const data = await permisosService.obtenerOverrides(userId);
      commit("SET_OVERRIDES", { userId, items: data.overrides || [] });
      return data;
    });
  },

  guardarOverrides({ commit }, { userId, items }) {
    return conGuardado(commit, async () => {
      const data = await permisosService.guardarOverrides(userId, items);
      commit("SET_OVERRIDES", { userId, items });
      return data;
    });
  },

  fetchPantallas({ commit }, idRol) {
    return conCarga(commit, "SET_PANTALLAS_LOADING", async () => {
      const data = await permisosService.obtenerPantallasDeRol(idRol);
      commit("SET_PANTALLAS", { idRol, rutas: data.rutas || [], menus: data.menus || [] });
      return data;
    });
  },

  guardarPantallas({ commit }, { idRol, rutas, menus }) {
    return conGuardado(commit, async () => {
      const data = await permisosService.guardarPantallasDeRol(idRol, { rutas, menus });
      commit("SET_PANTALLAS_ASIGNADAS", { rutas, menus });
      return data;
    });
  },

  cambiarRol({ commit }, { userId, idRol }) {
    return conGuardado(commit, () => permisosService.cambiarRol(userId, idRol));
  },

  // ─── Role CRUD: the write runs under `guardando`; the list refresh comes after it ──
  async crearRol({ commit, dispatch }, { rol, descripcion }) {
    const data = await conGuardado(commit, () => permisosService.crearRol({ rol, descripcion }));
    await refrescarRoles(dispatch);
    return data;
  },

  async actualizarRol({ commit, dispatch }, { idRol, rol, descripcion, activo }) {
    const data = await conGuardado(commit, () => permisosService.actualizarRol(idRol, { rol, descripcion, activo }));
    await refrescarRoles(dispatch);
    return data;
  },

  async eliminarRol({ commit, dispatch }, idRol) {
    const data = await conGuardado(commit, () => permisosService.eliminarRol(idRol));
    await refrescarRoles(dispatch);
    return data;
  },

  async clonarRol({ commit, dispatch }, { idRol, rol, descripcion }) {
    const data = await conGuardado(commit, () => permisosService.clonarRol(idRol, { rol, descripcion }));
    await refrescarRoles(dispatch);
    return data;
  },

  /**
   * Loads the current user's effective actions. Backend shape:
   * `{ acciones, overrides: { grant, deny }, catalogo_disponible, modo }`.
   * ANY failure fails open (agreed with the user): the UI shows everything and
   * the backend enforces. Never throws.
   */
  async cargarMisAcciones({ commit }, modulo) {
    try {
      const data = await permisosService.misAcciones(modulo);
      const overrides = data.overrides || {};
      commit("SET_MIS_ACCIONES", {
        acciones: data.acciones || [],
        grant: overrides.grant || [],
        deny: overrides.deny || [],
        catalogoDisponible: data.catalogo_disponible !== false,
        modo: data.modo || null,
      });
    } catch (error) {
      commit("SET_MIS_ACCIONES", { acciones: [], grant: [], deny: [], catalogoDisponible: false, modo: null });
    }
  },
};

const mutations = {
  SET_ROLES(state, roles) {
    state.roles = roles;
  },
  SET_ROLES_LOADING(state, value) {
    state.rolesLoading = value;
  },
  SET_ROLES_ERROR(state, error) {
    state.rolesError = error;
  },
  SET_CATALOGO(state, { acciones, total }) {
    state.catalogo.acciones = acciones;
    state.catalogo.total = total;
  },
  SET_CATALOGO_LOADING(state, value) {
    state.catalogo.loading = value;
  },
  SET_CATALOGO_ERROR(state, error) {
    state.catalogo.error = error;
  },
  SET_ROL_ACCIONES(state, { idRol, rol, claves }) {
    state.rolAcciones.idRol = idRol;
    state.rolAcciones.rol = rol;
    state.rolAcciones.claves = claves;
  },
  RESET_ROL_ACCIONES(state, idRol) {
    state.rolAcciones = { idRol, rol: null, claves: [], loading: true };
  },
  SET_ROL_ACCIONES_CLAVES(state, claves) {
    state.rolAcciones.claves = claves;
  },
  SET_ROL_ACCIONES_LOADING(state, value) {
    state.rolAcciones.loading = value;
  },
  SET_OVERRIDES(state, { userId, items }) {
    state.overrides.userId = userId;
    state.overrides.items = items;
  },
  RESET_OVERRIDES(state, userId) {
    state.overrides = { userId, items: [], loading: true };
  },
  SET_OVERRIDES_LOADING(state, value) {
    state.overrides.loading = value;
  },
  SET_PANTALLAS(state, { idRol, rutas, menus }) {
    state.pantallas.idRol = idRol;
    state.pantallas.rutas = rutas;
    state.pantallas.menus = menus;
  },
  /** Keeps the catalog shape; only the `asignada` flags follow the saved selection. */
  SET_PANTALLAS_ASIGNADAS(state, { rutas, menus }) {
    state.pantallas.rutas = state.pantallas.rutas.map((r) => ({ ...r, asignada: rutas.includes(r.ruta) ? 1 : 0 }));
    state.pantallas.menus = state.pantallas.menus.map((m) => ({ ...m, asignada: menus.includes(m.id_menu) ? 1 : 0 }));
  },
  SET_PANTALLAS_LOADING(state, value) {
    state.pantallas.loading = value;
  },
  SET_MIS_ACCIONES(state, { acciones, grant, deny, catalogoDisponible, modo = null }) {
    state.misAcciones = { acciones, grant, deny, catalogoDisponible, cargado: true, modo };
  },
  /** Called on root `logout` through the subscription in store/index.js. */
  RESET_ALL(state) {
    Object.assign(state, estadoInicial());
  },
  SET_GUARDANDO(state, value) {
    state.guardando = value;
  },
};

export default {
  namespaced: true,
  state: estadoInicial,
  getters,
  actions,
  mutations,
};
